import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Audiobook from "../components/Audiobook";

const SubjectShell = () => (
  <div className="p-10 animate-pulse">
    <div className="h-8 w-72 bg-slate-200 rounded mb-6" />
    <div className="h-4 w-48 bg-slate-200 rounded mb-3" />
    <div className="h-4 w-64 bg-slate-200 rounded mb-8" />
    <div className="h-48 bg-slate-200 rounded" />
  </div>
);

const normalizeId = (value) => {
  if (value == null) return "";
  if (typeof value === "object" && value.$oid) return String(value.$oid);
  return String(value);
};

const subjectMongoId = (subject) =>
  normalizeId(
    subject?._id ||
      subject?.subject_id ||
      subject?.subjectId ||
      subject?.mongo_id ||
      subject?.id
  );

const parseArrayIndex = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.trunc(parsed));
};

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const extractSubject = (json) => json?.subject || json?.item || json;

export default function SubjectDeepLink() {
  const {
    subjectId,
    partIndex: partIndexParam,
    sentenceIndex: sentenceIndexParam,
  } = useParams();
  const navigate = useNavigate();

  // These values are an opening instruction only. Audiobook captures them once
  // when it mounts and then owns all part/sentence navigation locally.
  const openingPartIndex = parseArrayIndex(partIndexParam);
  const openingSentenceIndex = parseArrayIndex(sentenceIndexParam);

  const [subject, setSubject] = useState(null);
  const [error, setError] = useState(null);
  const [uiReady, setUiReady] = useState(false);

  useEffect(() => {
    let alive = true;
    let readyTimer = null;
    const controller = new AbortController();
    const apiUrl = process.env.REACT_APP_USER_API_URL;

    const loadSubjectFromList = async () => {
      const response = await fetch(`${apiUrl}/subjects/list-books`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Could not load the library (${response.status}).`);
      }

      const json = await response.json();
      const subjects = Array.isArray(json)
        ? json
        : Array.isArray(json?.subjects)
          ? json.subjects
          : [];

      return (
        subjects.find(
          (candidate) => subjectMongoId(candidate) === normalizeId(subjectId)
        ) || null
      );
    };

    const loadSubject = async () => {
      const response = await fetch(
        `${apiUrl}/subjects/by-id/${encodeURIComponent(subjectId)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          signal: controller.signal,
        }
      );

      if (response.ok) {
        return extractSubject(await response.json());
      }

      // Compatibility fallback for a deployment where /by-id is not live yet.
      if (response.status === 404 || response.status === 405) {
        return loadSubjectFromList();
      }

      if (response.status === 401 || response.status === 403) {
        throw new Error("You do not have access to this audiobook.");
      }

      throw new Error(`Could not load the audiobook (${response.status}).`);
    };

    (async () => {
      try {
        setError(null);
        setSubject(null);
        setUiReady(false);

        if (!subjectId) {
          throw new Error("No audiobook ID was provided.");
        }

        const loadedSubject = await loadSubject();
        if (!alive) return;

        if (!loadedSubject) {
          throw new Error("This audiobook could not be found.");
        }

        if (
          loadedSubject.type &&
          String(loadedSubject.type).toLowerCase() !== "audiobook"
        ) {
          throw new Error("This shared subject is not an audiobook.");
        }

        setSubject(loadedSubject);

        readyTimer = window.setTimeout(() => {
          if (alive) setUiReady(true);
        }, 80);
      } catch (loadError) {
        if (loadError?.name === "AbortError") return;
        console.error("SubjectDeepLink load failed:", loadError);
        if (alive) {
          setError(loadError?.message || "Could not open this audiobook.");
        }
      }
    })();

    return () => {
      alive = false;
      controller.abort();
      if (readyTimer != null) window.clearTimeout(readyTimer);
    };
  }, [subjectId]);

  const handleBackToLibrary = useCallback(() => {
    navigate("/");
  }, [navigate]);

  if (error) {
    return (
      <div className="p-10">
        <div className="text-xl font-semibold mb-2">
          Couldn’t open audiobook
        </div>
        <div className="text-slate-600 mb-6">{error}</div>
        <button
          type="button"
          className="px-4 py-2 rounded-lg bg-slate-100 border border-slate-300"
          onClick={handleBackToLibrary}
        >
          Back to Library
        </button>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <div
        className={[
          "absolute inset-0 transition-opacity duration-300",
          uiReady ? "opacity-0 pointer-events-none" : "opacity-100",
        ].join(" ")}
        aria-hidden={uiReady}
      >
        <SubjectShell />
      </div>

      <div
        className={[
          "absolute inset-0 transition-opacity duration-300",
          uiReady ? "opacity-100" : "opacity-0 pointer-events-none",
        ].join(" ")}
        aria-hidden={!uiReady}
      >
        {subject ? (
          <Audiobook
            // Remount for a different subject, but not when same-subject URL
            // parameters change. That keeps the practice session independent.
            key={normalizeId(subjectId)}
            subject={subject}
            exerciseHandler={handleBackToLibrary}
            initialPartIndex={openingPartIndex}
            initialSentenceIndex={openingSentenceIndex}
          />
        ) : null}
      </div>
    </div>
  );
}